// Client layer (TS-2.5.03) - A-26's panel / BRD section 8.1's data-quality requirement. Composes
// the service hook (useDataQuality) with a severity-grouped, non-color-coded findings list.
// Findings are grouped Error > Warning > Info (DataQualityController.cs's own severity ordering).
// A finding links back to its project's detail page whenever the server attached a `subject`
// (DataQualitySubject: projectKey/projectName/owner) - D-229 item 5's fix: a Milestone/Note
// finding's own entityKey addresses THAT milestone/note, not a project, so the link target comes
// from subject.projectKey, never entityKey. Findings with no subject (D-149's two orphan
// vocabularies) render "-" in the Project column instead, as before. D-149 means two known
// vocabularies currently produce no findings; nothing here fabricates one to fill that gap.
//
// Layout (restyled onto the house card language, projects.css/admin.css's own precedent): a flex
// header (title + a muted "Generated" line), a toolbar card holding the two filters (each labeled
// above its select, like the Projects filter bar) plus the D-303 "Hide completed" checkbox, an
// optional muted "Portfolio totals" line (report.errorCount/warningCount/infoCount - whole-portfolio
// counts per D-297, labeled as such so they are never mistaken for the filtered view's own counts,
// and unaffected by hideCompleted below), and one card per severity group with the findings as a
// real <table> (Type/Finding/Project/Lead columns - Lead dropped while a lead filter is active,
// same rule as before) rather than the previous bare <ul>. Loading/error/empty states share one
// state card, same treatment as ProjectsPage's table-card.
//
// D-303: "Hide completed" is a standing view preference (remembered in localStorage per browser,
// mirroring ProjectsPage's own hideCompleted checkbox), not a typed filter - the server drops
// findings that belong to a now-hidden project but leaves orphan findings (no project) alone, and
// the "Portfolio totals" line above is explicitly whole-portfolio regardless of this checkbox.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { DataQualitySeverity, DataQualityWarning } from "../../api/generated/contracts";
import type { DataQualityParams } from "../../api/dataQuality";
import { SeverityBadge } from "./SeverityBadge";
import { useDataQuality } from "./hooks/useDataQuality";
import { useOwnerSuggestions } from "./hooks/useOwnerSuggestions";
import { readBooleanPreference, writeBooleanPreference } from "../../lib/booleanPreference";
import { strings } from "../../strings";
import "./dataQuality.css";

const SEVERITY_ORDER: readonly DataQualitySeverity[] = ["Error", "Warning", "Info"];

// D-303: localStorage key for this page's own "Hide completed" preference - separate from
// Projects' own key (ProjectsPage.tsx) since each page remembers its own choice.
const HIDE_COMPLETED_STORAGE_KEY = "eg.dataQuality.hideCompleted";

function groupBySeverity(warnings: readonly DataQualityWarning[]): Map<DataQualitySeverity, DataQualityWarning[]> {
  const groups = new Map<DataQualitySeverity, DataQualityWarning[]>();
  for (const severity of SEVERITY_ORDER) groups.set(severity, []);
  for (const warning of warnings) {
    groups.get(warning.severity)?.push(warning);
  }
  return groups;
}

export function DataQualityPage() {
  const [minimumSeverity, setMinimumSeverity] = useState<DataQualitySeverity>("Info");
  const [owner, setOwner] = useState("");
  // D-303: default OFF, restored from this browser's remembered choice if one was made.
  const [hideCompleted, setHideCompleted] = useState<boolean>(() => readBooleanPreference(HIDE_COMPLETED_STORAGE_KEY) ?? false);
  const { owners } = useOwnerSuggestions();

  const params = useMemo<DataQualityParams>(() => {
    const base: DataQualityParams = owner ? { minimumSeverity, owner } : { minimumSeverity };
    return hideCompleted ? { ...base, hideCompleted: true } : base;
  }, [minimumSeverity, owner, hideCompleted]);

  function handleHideCompletedChange(next: boolean) {
    setHideCompleted(next);
    writeBooleanPreference(HIDE_COMPLETED_STORAGE_KEY, next);
  }
  const { report, loading, error } = useDataQuality(params);

  const groups = report ? groupBySeverity(report.warnings) : null;
  const showStateCard = (loading && !report) || Boolean(error) || (report !== null && report.warnings.length === 0);

  return (
    <section className="eg-data-quality">
      <header className="eg-data-quality__header">
        <h1>{strings.dataQuality.heading}</h1>
        {report && (
          <p className="eg-data-quality__generated-at">
            {strings.dataQuality.generatedAt}: {new Date(report.generatedAtUtc).toLocaleString()}
          </p>
        )}
      </header>

      <div className="eg-data-quality__toolbar">
        <div className="eg-data-quality__filter">
          <label htmlFor="eg-dq-min-severity">{strings.dataQuality.minimumSeverity}</label>
          <select
            id="eg-dq-min-severity"
            value={minimumSeverity}
            onChange={(event) => setMinimumSeverity(event.target.value as DataQualitySeverity)}
          >
            {SEVERITY_ORDER.map((severity) => (
              <option key={severity} value={severity}>
                {strings.dataQuality.severities[severity]}
              </option>
            ))}
          </select>
        </div>
        <div className="eg-data-quality__filter">
          <label htmlFor="eg-dq-project-lead">{strings.dataQuality.projectLead}</label>
          <select id="eg-dq-project-lead" value={owner} onChange={(event) => setOwner(event.target.value)}>
            <option value="">{strings.dataQuality.allLeads}</option>
            {owners.map((candidate) => (
              <option key={candidate.displayName} value={candidate.displayName}>
                {candidate.displayName} ({candidate.projectCount})
              </option>
            ))}
          </select>
        </div>
        <div className="eg-data-quality__filter eg-data-quality__hide-completed">
          <label htmlFor="eg-dq-hide-completed" title={strings.common.hideCompletedHint}>
            <input
              id="eg-dq-hide-completed"
              type="checkbox"
              checked={hideCompleted}
              onChange={(event) => handleHideCompletedChange(event.target.checked)}
            />
            {strings.common.hideCompleted}
          </label>
        </div>
      </div>

      {showStateCard && (
        <div className="eg-data-quality__panel">
          {loading && !report && (
            <div role="status" aria-live="polite">
              {strings.dataQuality.loading}
            </div>
          )}

          {error && (
            <div role="alert">
              <p>{strings.dataQuality.errorTitle}</p>
              <p>{error.message}</p>
            </div>
          )}

          {report && report.warnings.length === 0 && <p>{strings.dataQuality.emptyState}</p>}
        </div>
      )}

      {report && report.warnings.length > 0 && (
        <>
          <p className="eg-data-quality__summary">
            {strings.dataQuality.summary.label}: {report.errorCount} {strings.dataQuality.summary.errors} ·{" "}
            {report.warningCount} {strings.dataQuality.summary.warnings} · {report.infoCount}{" "}
            {strings.dataQuality.summary.info}
          </p>

          {groups &&
            SEVERITY_ORDER.map((severity) => {
              const items = groups.get(severity) ?? [];
              if (items.length === 0) return null;
              const headingId = `eg-dq-group-${severity}`;

              return (
                <section key={severity} className="eg-data-quality__group" aria-labelledby={headingId}>
                  <h2 id={headingId}>
                    <SeverityBadge severity={severity} /> <span className="eg-data-quality__count">({items.length})</span>
                  </h2>
                  <table className="eg-data-quality-table">
                    <thead>
                      <tr>
                        <th scope="col">{strings.dataQuality.columns.type}</th>
                        <th scope="col">{strings.dataQuality.columns.finding}</th>
                        <th scope="col">{strings.dataQuality.columns.project}</th>
                        {!owner && <th scope="col">{strings.dataQuality.columns.lead}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((warning, index) => (
                        <tr key={`${warning.code}-${warning.entityKey ?? index}`}>
                          <td>
                            <span className="eg-data-quality__type">{warning.entityType}</span>
                          </td>
                          <td className="eg-data-quality__message">{warning.message}</td>
                          <td>
                            {warning.subject ? (
                              <Link to={`/projects/${warning.subject.projectKey}`}>{warning.subject.projectName}</Link>
                            ) : (
                              "—"
                            )}
                          </td>
                          {!owner && <td className="eg-data-quality__owner">{warning.subject?.owner ?? "—"}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              );
            })}
        </>
      )}
    </section>
  );
}
